import { sql } from "drizzle-orm"
import {
  boolean,
  index,
  integer,
  jsonb,
  pgSchema,
  timestamp,
  uniqueIndex,
  varchar,
  text,
} from "drizzle-orm/pg-core"
import { id_length, ulidId } from "./shared"

import {
  refCaption,
  refField,
  refList,
  refProcessGroup,
  refProcessGroupCaption,
} from "./reference"

export const templateSchema = pgSchema("template")

export const template = templateSchema.table("template", {
  id: ulidId(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

/**
 * The word version has a specific meaning in MPI. It does not match
   what we do in the backend. We store every save as a revision, but a
   version is a save where the version number changes.
 */
export const templateRevision = templateSchema.table(
  "template_version",
  {
    id: ulidId(),
    code: varchar("code", { length: 20 }).notNull(),
    title: varchar("title").notNull(),
    description: varchar("description").default(""),
    // "external description" is required on template profiles and needs to be surfaced in DFE
    displayedDescription: varchar("displayed_description")
      .notNull()
      .default(""),

    destinationMarket: varchar("destination_market"),
    businessArea: varchar("business_area").default(""),
    commodity: varchar("commodity"), // Array of commodity codes
    commodities: jsonb("commodities"), // Array of commodity codes (multiselect)
    certificateType: varchar("certificate_type").notNull(),
    templateType: varchar("template_type", {
      length: 10,
      enum: ["EXPORT", "IMPORT", "TRANSFER"],
    }),

    exportingCountry: varchar("exporting_country", { length: 2 }).default("NZ"),
    destinationCountries: jsonb("destination_countries"), // Array of country codes
    agencies: jsonb("agencies"), // Array of agencies

    signatory: varchar("signatory"),
    declarationHeading: varchar("declaration_heading", {
      enum: ["HEALTH_ATTESTATION", "CUSTOM", "NONE"],
    }),
    declarationHeadingOther: varchar("declaration_heading_other"),
    competentAuthority: varchar("competent_authority", {
      enum: [
        "MINISTRY_FOR_PRIMARY_INDUSTRIES",
        "CUSTOM",
        "NEW_ZEALAND_FOOD_SAFETY_AUTHORITY",
        "NONE",
      ],
    }),
    competentAuthorityOther: varchar("competent_authority_other"),

    industryAccess: boolean("industry_access").notNull().default(false), // Allows templates only for internal use to be suppressed from the DFE
    ocaAccess: boolean("oca_access").notNull().default(false), // Allows border inspectors to access certificates created with template
    allowForeignPremises: boolean("allow_foreign_premises")
      .notNull()
      .default(false),
    allowPackageLevels: boolean("allow_package_levels")
      .notNull()
      .default(false),

    languageAllEntered: boolean("language_all_entered")
      .notNull()
      .default(false),
    languageDischargePort: boolean("language_discharge_port")
      .notNull()
      .default(false),
    languageImportingCountry: boolean("language_importing_country")
      .notNull()
      .default(false),
    languageInspectionPort: boolean("language_inspection_port")
      .notNull()
      .default(false),
    languageListedLanguages: boolean("language_listed_languages")
      .notNull()
      .default(false),
    languageList: jsonb("language_list"), // ordered array of lang codes for translations, e.g. ["fr", "de"]. Usually only one.

    submissionLayout: varchar("submission_layout", {
      length: 15,
      enum: [
        "CUSTOM", // BUSINESS_RULES
        "AP_TRANSFER",
        "AP_EXPORT",
        "PHYTO_EXPORT",
        "IMPORT",
      ],
    })
      .notNull()
      .default("CUSTOM"),
    pdfLayout: varchar("pdf_layout", {
      enum: [
        "ADOBE",
        "NZ",
        "CATCH",
        "NZ_2024",
        "NZ_RTL",
        "NZ_RTL_HALAL",
        "EU_EXPORT_2021",
        "EU_EXPORT_2019",
        "EU_TRANSIT_2021",
        "EU_EXPORT_2015",
        "APA_REGISTRATION",
        "EU_EXPORT",
        "EU_TRANSIT",
        "EU_CH_BEEF",
        "CU_ANIMAL_PRODUCTS",
        "RUSSIAN_SEAFOOD",
        "RUSSIAN_DAIRY",
      ],
    })
      .notNull()
      .default("ADOBE"),
    pdfAddendum: varchar("pdf_addendum", {
      enum: ["NONE", "CATCH", "CATCH_JAPAN", "CATCH_US", "CATCH_EU"],
    }),
    producePDF: boolean("produce_pdf").notNull().default(true),
    xmlLayout: varchar("xml_layout", {
      enum: [
        "NONE",
        "UNCEFACT",
        "EU_IMPORT_XML",
        "EU_TRANSIT_XML",
        "EU_CATCH_XML",
        "IPPC_PHYTO_XML",
        "OFFICIAL_2_9_1",
        "EU_PROC_STATEMENT_XML",
      ],
    }),
    destinationSystem: varchar("destination_system", {
      enum: [
        "NONE",
        "EU_TRACES_IMPORT",
        "EU_TRACES_CATCH",
        "EU_TRACES_PROCESSING_STATEMENT",
      ],
    }).default("NONE"),
    sendToTraces: boolean("send_to_traces").notNull().default(false),
    forceNZCountryOfOrigin: boolean("force_nz_country_of_origin")
      .notNull()
      .default(false),

    outputTitle: varchar("output_title"),
    outputTitleTranslations: jsonb("output_title_translations"),
    outputSubtitle: varchar("output_subtitle"),
    outputSubtitleTranslations: jsonb("output_subtitle_translations"),

    documentId: varchar("document_id"),
    majorVersion: integer("major_version").notNull().default(1),
    minorVersion: integer("minor_version").notNull().default(0),
    officialVersion: integer("official_version"), // Used for PDF and XML generation, leave blank to suppress version number
    uploadInProgress: boolean("upload_in_progress").notNull().default(false),
    newFileUpload: boolean("new_file_upload").notNull().default(false),

    status: varchar("status").notNull(),
    release: timestamp("release_date"),
    effective: timestamp("effective"),
    expire: timestamp("expire"),
    reasonForUpdate: varchar("reason_for_update"),

    assignedTo: varchar("assigned_to").notNull().default(""),
    updatedBy: varchar("updated_by").notNull().default(""),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
    templateId: varchar("template_id", { length: id_length })
      .notNull()
      .references(() => template.id, { onDelete: "cascade" }),
  },
  (table) => {
    return {
      templateId: index("idx_template_id").on(table.templateId),
      // NOTE: Removed unique constraint to allow multiple approved templates
      // onlyOneApproved: uniqueIndex("idx_approved_template_revision")
      //   .onOnly(table.templateId, table.status, table.majorVersion)
      //   .where(sql`status = 'APPROVED'`),
      // canFindUniqueTemplate: uniqueIndex("idx_can_find_template").onOnly(
      //   table.certificateType,
      //   table.destinationMarket,
      //   table.commodity,
      //   table.status, // TODO - strip back unique condition to only apply to organics/free sale/wine.
      //   // Default behaviour is nulls not distinct, so null for destinationMarket or commodity will satisfy uniqueIndex
      // ),
      //.using(sql`status = 'APPROVED'`),
      documentIdUnique: index("idx_document_id_is_unique")
        .onOnly(table.documentId)
        .where(sql`document_id is not null`),
    }
  },
)

export const templateRevisionStatus = templateSchema.table(
  "template_revision_status",
  {
    id: ulidId(),
    status: varchar("status").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    createdBy: varchar("created_by").notNull().default(""),
    templateId: varchar("template_id", { length: id_length })
      .notNull()
      .references(() => template.id, { onDelete: "cascade" }),
    templateVersionId: varchar("template_version_id", { length: id_length })
      .notNull()
      .references(() => templateRevision.id, { onDelete: "cascade" }),
  },
)

export const templateField = templateSchema.table(
  "template_field",
  {
    id: ulidId(), // prefix tfd-
    templateVersionId: varchar("template_version_id", { length: id_length })
      .notNull()
      .references(() => templateRevision.id, { onDelete: "cascade" }),
    fieldUniqueId: varchar("field_unique_id", { length: 50 }) // fuid
      .notNull()
      .references(() => refField.fieldUniqueId, {
        onDelete: "no action",
        onUpdate: "cascade",
      }), // Can't delete fuid that is already in use

    // Submission Layout configuration
    inSubmission: boolean("in_submission"),
    mandatory: boolean("mandatory"),
    submissionCaption: varchar("submission_caption", { length: 255 }),
    submissionDisplayType: varchar("submission_display_type", {
      length: 20,
      enum: ["FREE_TEXT", "RESTRICTED_LIST", "UNRESTRICTED_LIST"],
    }),
    maxLength: integer("max_length"),
    referenceDataListId: varchar("list_code", { length: 40 }).references(
      () => refList.listCode,
      { onDelete: "no action", onUpdate: "cascade" },
    ),
    listColumnsToDisplay: varchar("list_columns", {
      length: 15,
      enum: ["TEXT", "CODE", "CODE_AND_TEXT"],
    }),
    listSortOrder: varchar("list_sort_order", {
      length: 15,
      enum: ["CODE", "TEXT"],
    }),
    customLookup: varchar("custom_lookup", {
      length: 20,
      enum: ["NONE", "EU_OPERATOR_SEARCH"],
    }).default("NONE"),
    sequenceNumber: integer("sequence_number"), // defined for classifications, where user can configure field order within master layout
    formOrder: integer("form_order"), // user-defined form order for field display
    pdfOrder: integer("pdf_order"), // user-defined PDF order for product classifications
    xmlOrder: integer("xml_order"), // user-defined XML order for product classifications

    // XML Layout configuration
    inXml: boolean("in_xml"),
    xmlCaptionId: varchar("xml_caption_id", { length: id_length }).references(
      () => refCaption.id,
      { onDelete: "no action" },
    ),
    xmlDefaultOption: varchar("xml_default_option", {
      length: 16,
      enum: ["NOT_APPLICABLE", "XXXXXX", "TAG_NOT_INCLUDED"],
    }),
    xmlColumnsToDisplay: varchar("xml_columns", {
      length: 15,
      enum: ["TEXT", "CODE", "CODE_AND_TEXT"],
    }),

    // PDF Layout configuration
    inPdf: boolean("in_pdf"),
    pdfCaptionId: varchar("pdf_caption_id", { length: id_length }).references(
      () => refCaption.id,
      { onDelete: "no action" },
    ),
    pdfDefaultOption: varchar("pdf_default_option", {
      length: 15,
      enum: ["NOT_APPLICABLE", "XXXXXX", "BLANK", "NOT_SHOWN"],
    }),
    pdfBoxWidth: varchar("pdf_box_width", {
      length: 8,
      enum: ["FULL", "HALF", "THIRD", "QUARTER"],
    }),
    pdfColumnsToDisplay: varchar("pdf_columns", {
      length: 15,
      enum: ["TEXT", "CODE", "CODE_AND_TEXT"],
    }),

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by"), // ID for user
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by"), // ID for user
  },
  (table) => {
    return {
      templateFieldId: uniqueIndex("idx_template_id_field_id").on(
        table.templateVersionId,
        table.fieldUniqueId,
      ),
    }
  },
)

export const templateDeclarationGroup = templateSchema.table(
  "template_declaration_group",
  {
    id: ulidId(), // prefix tdg-
    templateVersionId: varchar("template_version_id", { length: id_length })
      .notNull()
      .references(() => templateRevision.id, { onDelete: "cascade" }),

    groupSequenceNumber: integer("sequence_number").notNull(), // sequential integer starting at 0, 1, 2, ...

    groupType: varchar("group_type", {
      length: 25,
      enum: [
        "PHRASE",
        "MANDATORY",
        "MANDATORY_EXACTLY_ONE",
        "MANDATORY_ONE_OR_MORE",
        "OPTIONAL_ZERO_OR_ONE",
        "OPTIONAL_ZERO_OR_MORE",
        "FOOTNOTE",
      ],
    }).notNull(),

    numbering: varchar("numbering", {
      length: 15,
      enum: [
        "NUMBER", // 1, 2, ..
        "LOWER_ALPHA", // a, b, ..
        "UPPER_ALPHA", // A, B, ..
        "LOWER_ROMAN", // i, ii, ..
        "UPPER_ROMAN", // I, II, ..
        "CUSTOM",
        "BULLET",
        "NONE",
      ],
    }).notNull(),
    applySameNumbering: boolean("apply_same_numbering")
      .notNull()
      .default(false),
    startAt: integer("start_at").default(-1).notNull(),

    indent: integer("indent"), // integer 0-4

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by"), // ID for user
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by"), // ID for user
  },
  (table) => {
    return {
      templateDeclarationGroupId: index("idx_template_id_decl_group_id").on(
        table.templateVersionId,
        table.groupSequenceNumber,
      ),
    }
  },
)

export const templateDeclarationStatement = templateSchema.table(
  "template_statement",
  {
    id: ulidId(), // prefix tds-
    declarationGroupId: varchar("declaration_group_id", { length: id_length })
      .notNull()
      .references(() => templateDeclarationGroup.id, { onDelete: "cascade" }),

    statementCode: integer("statement_code").notNull(), // Unique statement id number within the template version starting at 1, 2, 3
    statementSequenceNumber: integer("sequence_number").notNull(), // sequential integer within the group starting at 0, 1, 2, ...

    statementText: varchar("statement_text"),
    statementTextTranslations: jsonb("statement_text_translations"),

    customNumber: varchar("custom_number", { length: 20 }),

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by"), // ID for user
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by"), // ID for user
  },
  (table) => {
    return {
      templateDeclarationStatementId: index(
        "idx_template_decl_group_statement_id",
      ).on(table.declarationGroupId, table.statementSequenceNumber),
    }
  },
)

// This table structure is TBD:
export const templateDeclarationStatementInput = templateSchema.table(
  "template_statement_input",
  {
    id: ulidId(), // prefix tdi-
    declarationStatementId: varchar("declaration_statement_id", {
      length: id_length,
    })
      .notNull()
      .references(() => templateDeclarationStatement.id, {
        onDelete: "cascade",
      }),

    // TODO - determine if should be just for user input or for all
    inputType: varchar("input_type", {
      length: 22,
      enum: [
        "user",
        "sourceED",
        "sourceEDDOI",
        "overseasMarket",
        "companyName",
        "companyAddress",
        "manufacturerName",
        "defaultTracesStatement",
      ],
    }).default("user"),

    inputSequenceNumber: integer("sequence_number").notNull(),
    name: varchar("name", { length: 100 }),
    maxLength: integer("max_length"),
    tracesId: varchar("traces_id", { length: 50 }),

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by"), // ID for user
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by"), // ID for user
  },
  (table) => {
    return {
      templateDeclarationInputId: index(
        "idx_template_decl_statement_input_id",
      ).on(table.declarationStatementId, table.inputSequenceNumber),
    }
  },
)

export const templateDeclarationStatementTraces = templateSchema.table(
  "template_statement_traces",
  {
    id: ulidId(), // prefix tdt-
    declarationStatementId: varchar("declaration_statement_id", {
      length: id_length,
    })
      .notNull()
      .references(() => templateDeclarationStatement.id, {
        onDelete: "cascade",
      }),

    tracesId: varchar("traces_id", { length: 50 }),
    tracesContent: varchar("traces_content", { length: 50 }),

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by"), // ID for user
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by"), // ID for user
  },
  (table) => {
    return {
      templateTracesId: uniqueIndex(
        "idx_template_id_decl_statement_traces_id",
      ).on(table.declarationStatementId, table.id),
    }
  },
)

export const templateProcessGroup = templateSchema.table(
  "template_process_group",
  {
    id: ulidId(), // prefix tpg-
    templateVersionId: varchar("template_version_id", { length: id_length })
      .notNull()
      .references(() => templateRevision.id, { onDelete: "cascade" }),
    refProcessGroupId: integer("process_group_id")
      .notNull()
      .references(() => refProcessGroup.id, {
        onDelete: "no action",
        onUpdate: "no action",
      }),

    dateFormat: varchar("date_format", { length: 10, enum: ["D_M_Y", "M_Y"] }),
    dateRange: varchar("date_range", {
      length: 20,
      enum: ["ALL", "LAST_IN_EACH_PRODUCT"],
    }),

    premisesAddressFormat: varchar("premises_address_format", {
      length: 10,
      enum: ["SHORT", "MEDIUM", "LONG"],
    }),
    premisesRange: varchar("premises_range", {
      length: 20,
      enum: ["ALL", "LAST_IN_EACH_PRODUCT"],
    }),

    defaultAction: varchar("default_action", {
      length: 20,
      enum: [
        "BLANK",
        "NOT_SHOWN",
        "ENTRY_REQUIRED",
        "NOT_APPLICABLE",
        "XXXXXX",
      ],
    }).notNull(),
    displayedBoxWidth: varchar("displayed_box_width", {
      length: 20,
      enum: ["FULL", "HALF", "THIRD", "QUARTER"],
    }).notNull(),
    includeOverseasProcesses: boolean("include_overseas_processes").notNull(),

    operatorActivityType: varchar("operator_activity_type", { length: 50 }),

    sequenceNumber: integer("sequence_number").notNull(), // display sort order, sequential integer starting from 0

    // XML Layout configuration
    inXml: boolean("in_xml"),
    xmlCaptionId: integer("xml_caption_id").references(
      () => refProcessGroupCaption.id,
      { onDelete: "no action" },
    ),

    // PDF Layout configuration
    inPdf: boolean("in_pdf"),
    pdfCaptionId: integer("pdf_caption_id").references(
      () => refProcessGroupCaption.id,
      { onDelete: "no action" },
    ),

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by"), // ID for user
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by"), // ID for user
  },
  (table) => {
    return {
      templateProcessGroup: uniqueIndex("idx_template_process_group").on(
        table.templateVersionId,
        table.refProcessGroupId,
      ),
    }
  },
)
export const templateSupportingDocument = templateSchema.table(
  "template_supporting_document",
  {
    id: ulidId(),
    templateVersionId: varchar("template_version_id", { length: id_length })
      .notNull()
      .references(() => templateRevision.id, { onDelete: "cascade" }),
    documentType: varchar("document_type", {
      enum: [
        "FIC",
        "IP",
        "PAC",
        "ADH",
        "ADM",
        "INSP",
        "DOC",
        "OPC",
        "TRT",
        "ORG",
        "OMA",
        "SLAB",
        "OTHER",
      ],
    }).notNull(),
    includeInXml: boolean("include_in_xml").notNull().default(false),
    printWithPdf: boolean("print_with_pdf").notNull().default(false),

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by"), // ID for user
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by"), // ID for user
  },
  (table) => {
    return {
      templateSupportingDocumentUnique: uniqueIndex(
        "idx_template_supporting_document_unique",
      ).on(table.templateVersionId, table.documentType),
    }
  },
)

export const templateFieldReferences = templateSchema.table(
  "field_references",
  {
    templateCode: varchar("template_code", { length: 20 }),
    refCatCode: varchar("ref_cat_code", { length: 255 }),
    refTypeCode: varchar("ref_type_code", { length: 255 }),
    seqNo: integer("seq_no"),
  },
  (table) => {
    return {
      templateCodeIdx: index("idx_field_references_template_code").on(
        table.templateCode,
      ),
    }
  },
)

export const templateAuditEvent = templateSchema.table(
  "template_audit_event",
  {
    id: ulidId(),
    templateId: varchar("template_id", { length: id_length })
      .notNull()
      .references(() => template.id, { onDelete: "no action" }),
    templateVersionId: varchar("template_version_id", {
      length: id_length,
    }).notNull(),
    templateCode: varchar("template_code", { length: 50 }).notNull(),
    majorVersion: integer("major_version").notNull(),
    minorVersion: integer("minor_version").notNull(),
    status: varchar("status", { length: 20 }).notNull(),
    changeCategory: varchar("change_category", {
      length: 50,
      enum: [
        "StatusOnly",
        "ScheduleOnly",
        "GeneralOnly",
        "ScheduleAndStatus",
        "ScheduleAndGeneral",
        "StatusAndGeneral",
        "ScheduleStatusAndGeneral",
        "Created",
        "CopiedFrom",
        "CopiedTo",
        "TabSpecific",
      ],
    }).notNull(),
    detail: varchar("detail", { length: 500 }).notNull(),
    comment: text("comment"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by", { length: 255 }).notNull(),
  },
  (table) => {
    return {
      templateVersionIdx: index("idx_template_audit_event_template_version").on(
        table.templateId,
        table.majorVersion,
      ),
      createdAtIdx: index("idx_template_audit_event_created_at").on(
        table.createdAt,
      ),
    }
  },
)
